import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import StudentSelector from './components/StudentSelector';
import IncidentEntryForm from './components/IncidentEntryForm';
import IncidentTimeline from './components/IncidentTimeline';
import IncidentReportTable from './components/IncidentReportTable';
import BehaviorAnalytics from './components/BehaviorAnalytics';
import CollaborationPanel from './components/CollaborationPanel';

import { useIncidents } from 'context/IncidentsContext';
import { useStudents } from 'context/StudentsContext';
import { useUserProfiles } from 'context/UserProfilesContext';
import { deriveNameFromEmail } from 'lib/currentUser';
import { downloadIncidentsCsv } from 'lib/behaviorIncidentsCsv';
import { humanizeSupabaseError } from '../../lib/supabaseErrors';

const BehavioralIncidentLogging = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const preselectedStudentIdFromState =
    location?.state?.selectedStudentId || location?.state?.studentId || '';

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeTab, setActiveTab] = useState('entry');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [exportNotice, setExportNotice] = useState(null);

  const { incidents, isLive, addIncident, incidentsForStudent } = useIncidents();
  const { students } = useStudents();
  const { getUserById } = useUserProfiles();

  // Deep-link support: the Collect Data launcher and other "log this
  // incident for student X" callers push { selectedStudentId } into
  // location.state. If the roster has that student, preselect them and
  // land on the Log Incident tab.
  useEffect(() => {
    if (!preselectedStudentIdFromState) return;
    if (selectedStudent?.id === preselectedStudentIdFromState) return;
    const match = (Array.isArray(students) ? students : []).find(
      (s) => s.id === preselectedStudentIdFromState,
    );
    if (!match) return;
    setSelectedStudent({
      id: match.id,
      firstName: match.firstName || '',
      lastName: match.lastName || '',
      name: `${match.firstName || ''} ${match.lastName || ''}`.trim() || match.id,
      grade: match.grade || '',
      avatar: match.photo || null,
      iepStatus: 'Active',
    });
    setActiveTab('entry');
    // Clear the location state so a refresh doesn't keep re-driving the
    // preselection.
    navigate(location.pathname, { replace: true, state: null });
  }, [
    preselectedStudentIdFromState,
    students,
    selectedStudent,
    navigate,
    location.pathname,
  ]);

  // If the user has exactly one student on their roster, preselect them —
  // that's a friendly shortcut and it points at a real record. Otherwise
  // leave the selector empty and let the user pick, rather than silently
  // logging incidents against a fictional "Alex Thompson" that isn't in the
  // students table (which is what the previous auto-select mock did).
  useEffect(() => {
    if (selectedStudent) return;
    if (!Array.isArray(students) || students.length !== 1) return;
    const only = students[0];
    setSelectedStudent({
      id: only.id,
      firstName: only.firstName || '',
      lastName: only.lastName || '',
      name: `${only.firstName || ''} ${only.lastName || ''}`.trim() || only.id,
      grade: only.grade || '',
      avatar: only.photo || null,
      iepStatus: 'Active',
    });
  }, [selectedStudent, students]);

  const studentIncidents = useMemo(() => {
    if (!selectedStudent) return [];
    return incidentsForStudent(selectedStudent.id);
  }, [selectedStudent, incidentsForStudent, incidents]);

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
  };

  const handleIncidentSubmit = async (incidentData) => {
    if (!selectedStudent) return;
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      await addIncident({
        ...incidentData,
        studentId: selectedStudent.id,
      });
      setSubmitSuccess('Incident logged.');
      setActiveTab('timeline');
    } catch (error) {
      // Surface a real error to the teacher instead of failing silently.
      // The previous console.error swallowed everything so RLS violations,
      // missing tables, and network failures all looked identical (nothing
      // happens after clicking Save).
      setSubmitError(
        humanizeSupabaseError(
          error,
          'Could not log this incident. Please check your connection and try again.',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIncidentSelect = (incident) => {
    console.log('Selected incident for details:', incident);
  };

  const handleCollaborationUpdate = (update) => {
    console.log('Collaboration update:', update);
  };

  // Top-of-page Export button. Historically this was a dead <Button>
  // with no onClick — users clicking the obvious "Export" got nothing
  // and reported "Behavioral Incident Logging can not export". It now
  // downloads a CSV of the currently-visible incidents:
  //   - a student is selected → that student's incidents
  //   - no student selected   → every incident on the caseload
  // Both paths enrich each row with a resolved reporter display name and
  // (in the "everyone" case) the student name, so the CSV is legible in
  // Excel without cross-referencing IDs.
  const studentIndex = useMemo(() => {
    const out = {};
    for (const s of Array.isArray(students) ? students : []) {
      if (!s?.id) continue;
      out[s.id] =
        `${s.firstName || ''} ${s.lastName || ''}`.trim() ||
        s.name ||
        s.id;
    }
    return out;
  }, [students]);

  const handleExportCsv = () => {
    const source = selectedStudent
      ? studentIncidents
      : Array.isArray(incidents)
        ? incidents
        : [];

    const enriched = source.map((incident) => {
      const reporter = getUserById(incident?.reportedBy);
      const reporterName =
        reporter?.displayName ||
        deriveNameFromEmail(reporter?.email) ||
        (incident?.reportedBy ? 'A teammate' : '');
      return {
        ...incident,
        _reporterName: reporterName,
        _studentName:
          incident?.studentName || studentIndex[incident?.studentId] || '',
      };
    });

    const studentName = selectedStudent
      ? selectedStudent.name ||
        `${selectedStudent.firstName || ''} ${selectedStudent.lastName || ''}`.trim()
      : null;

    const { rowCount, filename } = downloadIncidentsCsv({
      rows: enriched,
      studentName,
    });

    setSubmitError(null);
    setExportNotice(
      rowCount === 0
        ? `${filename} downloaded — no incidents in the current view yet, so the file only contains column headers.`
        : `${filename} downloaded (${rowCount} incident${rowCount === 1 ? '' : 's'}).`,
    );
  };

  const tabOptions = [
    { id: 'entry', label: 'Log Incident', icon: 'Plus' },
    { id: 'timeline', label: 'Timeline', icon: 'Clock' },
    { id: 'report', label: 'Data Table', icon: 'Table' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'collaboration', label: 'Collaboration', icon: 'Users' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main
        className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        } pt-16`}
      >
        <div className="p-6">
          <Breadcrumb />

          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Behavioral Incident Logging</h1>
              <p className="text-muted-foreground mt-2">
                Structured ABC data capture + function-of-behavior hypothesis.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div
                className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${
                  isLive
                    ? 'bg-success/10 text-success border-success/30'
                    : 'bg-warning/10 text-warning border-warning/30'
                }`}
                title={isLive ? 'Connected to Supabase' : 'Local demo mode — data stays in this browser'}
              >
                <Icon name={isLive ? 'Cloud' : 'HardDrive'} size={14} />
                {isLive ? 'Live sync' : 'Demo mode'}
              </div>
              <Button
                variant="outline"
                onClick={handleExportCsv}
                title={
                  selectedStudent
                    ? `Download a CSV of every incident logged for ${
                        selectedStudent.firstName || selectedStudent.name || 'this student'
                      }`
                    : 'Download a CSV of every incident logged across all students'
                }
              >
                <Icon name="Download" size={16} />
                Export CSV
              </Button>
            </div>
          </div>

          {submitError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 whitespace-pre-line">
              {submitError}
            </div>
          )}
          {submitSuccess && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              {submitSuccess}
            </div>
          )}
          {exportNotice && (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <Icon name="Download" size={16} className="mt-0.5" />
                <span>{exportNotice}</span>
              </div>
              <button
                type="button"
                className="text-blue-700 hover:text-blue-900 text-xs font-medium"
                onClick={() => setExportNotice(null)}
              >
                Dismiss
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <div className="xl:col-span-5 space-y-6">
              <StudentSelector
                selectedStudent={selectedStudent}
                onStudentSelect={handleStudentSelect}
                onCreateNew={() => console.log('Navigate to create new student profile')}
              />

              <div className="xl:hidden">
                <div className="flex overflow-x-auto gap-1 p-1 bg-muted rounded-lg">
                  {tabOptions.map((tab) => (
                    <Button
                      key={tab.id}
                      variant={activeTab === tab.id ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setActiveTab(tab.id)}
                      className="flex-shrink-0"
                    >
                      <Icon name={tab.icon} size={16} />
                      {tab.label}
                    </Button>
                  ))}
                </div>
              </div>

              {(activeTab === 'entry' || (typeof window !== 'undefined' && window.innerWidth >= 1280)) &&
                selectedStudent && (
                  <IncidentEntryForm
                    selectedStudent={selectedStudent}
                    onIncidentSubmit={handleIncidentSubmit}
                    isSubmitting={isSubmitting}
                  />
                )}

              {activeTab === 'timeline' && typeof window !== 'undefined' && window.innerWidth < 1280 && (
                <IncidentTimeline
                  incidents={studentIncidents}
                  selectedStudent={selectedStudent}
                  onIncidentSelect={handleIncidentSelect}
                />
              )}

              {activeTab === 'report' && typeof window !== 'undefined' && window.innerWidth < 1280 && (
                <IncidentReportTable
                  incidents={studentIncidents}
                  selectedStudent={selectedStudent}
                />
              )}

              {activeTab === 'analytics' && typeof window !== 'undefined' && window.innerWidth < 1280 && (
                <BehaviorAnalytics incidents={studentIncidents} selectedStudent={selectedStudent} />
              )}

              {activeTab === 'collaboration' && typeof window !== 'undefined' && window.innerWidth < 1280 && (
                <CollaborationPanel
                  selectedStudent={selectedStudent}
                  onCollaborationUpdate={handleCollaborationUpdate}
                />
              )}
            </div>

            <div className="hidden xl:block xl:col-span-7 space-y-6">
              {selectedStudent ? (
                <>
                  <IncidentTimeline
                    incidents={studentIncidents}
                    selectedStudent={selectedStudent}
                    onIncidentSelect={handleIncidentSelect}
                  />
                  <IncidentReportTable
                    incidents={studentIncidents}
                    selectedStudent={selectedStudent}
                  />
                  <BehaviorAnalytics
                    incidents={studentIncidents}
                    selectedStudent={selectedStudent}
                  />
                  <CollaborationPanel
                    selectedStudent={selectedStudent}
                    onCollaborationUpdate={handleCollaborationUpdate}
                  />
                </>
              ) : (
                <div className="bg-card rounded-lg border border-border p-12 text-center">
                  <Icon
                    name="UserCircle"
                    size={64}
                    className="text-muted-foreground mx-auto mb-4 opacity-50"
                  />
                  <h3 className="text-lg font-medium text-card-foreground mb-2">
                    Select a Student
                  </h3>
                  <p className="text-muted-foreground">
                    Choose a student from the list to begin logging behavioral incidents and viewing analytics
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BehavioralIncidentLogging;
